/**
 * /payroll/year-end/w2/:year — W-2 batch generation + IRIS transmit.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { api, getToken } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";
import { payrollEmployeesQueryOptions } from "@/lib/queries";
import type { PayrollEmployee } from "@/lib/types";

export const Route = createFileRoute("/_auth/payroll/year-end_/w2/$year")({
  component: W2BatchPage,
});

function W2BatchPage() {
  const { year } = Route.useParams();
  const yr = Number(year);
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState<any | null>(null);

  const { data: employees = [], isLoading } = useQuery(payrollEmployeesQueryOptions(true));

  const transmit = useMutation({
    mutationFn: async () => {
      return await api(`/api/payroll/year-end/w2/${yr}/transmit`, { method: "POST" });
    },
    onSuccess: (r) => {
      setResult(r);
      setConfirm(false);
      qc.invalidateQueries({ queryKey: ["payroll", "year-end"] });
    },
  });

  const downloadPdf = (id: string, name: string, copy = "B") => {
    const url = `/api/payroll/year-end/w2/${yr}/${id}.pdf?copy=${copy}`;
    fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u;
        a.download = `W2-${yr}-${name}.pdf`;
        a.click();
        URL.revokeObjectURL(u);
      });
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <IconButton component={Link} to="/payroll/year-end" size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="caption" color="text.secondary">
          Back to Year-End
        </Typography>
      </Stack>
      <Typography variant="h4" fontWeight={700} mb={1}>
        W-2 Batch — Tax Year {yr}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        One W-2 per active employee. Click the row to preview/download the employee copy. Use{" "}
        <strong>Transmit to IRIS</strong> to file Copy A with the SSA.
      </Typography>
      <Box sx={{ mb: 3 }}>
        <HelpHint docPath="payroll-year-end/filing-w2.md" label="W-2 filing guide" />
      </Box>

      <Stack direction="row" spacing={2} mb={3}>
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={() => setConfirm(true)}
          disabled={employees.length === 0}
        >
          Transmit all W-2s to IRIS
        </Button>
        <Button
          component={Link}
          to={`/payroll/year-end/w3/${yr}`}
          variant="outlined"
        >
          Generate W-3 Transmittal
        </Button>
      </Stack>

      {result && (
        <Alert
          severity={result.status === "rejected" ? "error" : result.status === "dry_run" ? "warning" : "success"}
          sx={{ mb: 3 }}
        >
          <strong>{result.status?.toUpperCase()}</strong> — submission{" "}
          <code>{result.submission_id}</code>, {result.record_count} records.
          {result.error && <Box sx={{ mt: 1 }}>{result.error}</Box>}
        </Alert>
      )}

      {isLoading ? (
        <CircularProgress />
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>SSN (last 4)</TableCell>
                <TableCell>State</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(employees as PayrollEmployee[]).map((e) => (
                <TableRow key={e.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {e.last_name}, {e.first_name}
                    </Typography>
                  </TableCell>
                  <TableCell>{(e as any).ssn_last4 || "—"}</TableCell>
                  <TableCell>{(e as any).address_state || "—"}</TableCell>
                  <TableCell>
                    <Chip label={e.is_active ? "Active" : "Inactive"} size="small" />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Download Copy B (employee federal copy)">
                      <Button
                        size="small"
                        startIcon={<PictureAsPdfIcon />}
                        onClick={() => downloadPdf(e.id, e.last_name, "B")}
                      >
                        Copy B
                      </Button>
                    </Tooltip>
                    <Tooltip title="Download Copy C (employee records)">
                      <Button
                        size="small"
                        onClick={() => downloadPdf(e.id, e.last_name, "C")}
                      >
                        Copy C
                      </Button>
                    </Tooltip>
                    <Tooltip title="Download Copy 2 (state/local return)">
                      <Button
                        size="small"
                        onClick={() => downloadPdf(e.id, e.last_name, "2")}
                      >
                        Copy 2
                      </Button>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {employees.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No active employees found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={confirm} onClose={() => setConfirm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Transmit W-2 Batch to IRS IRIS?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            This will build a batch payload of all {employees.length} active employees' W-2 forms
            and submit to the IRS Information Returns Intake System (IRIS).
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            If <code>iris_environment</code> is set to <code>test</code>, no actual transmission
            will occur — the payload will be validated and a synthetic ack returned.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => transmit.mutate()}
            disabled={transmit.isPending}
          >
            {transmit.isPending ? <CircularProgress size={18} /> : "Transmit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
